import { useMemo } from 'react';
import { PlaidTransaction } from '../types/transactions';
import {
  normalizeMerchant,
  canonicalizeKeys,
  deriveSemanticType,
  deriveIsSelf,
  SemanticType,
  NormalizationSource,
} from '../utils/merchant-normalizer';

// ─── Types ─────────────────────────────────────────────────────────────────────

export interface MerchantGroupedRow {
  /** Unique stable key: "YYYY-MM|merchantKey|INCOME|CHECK|false" */
  groupKey: string;
  /** YYYY-MM */
  month: string;
  /** Title-cased display name */
  normalizedMerchant: string;
  direction: 'INCOME' | 'EXPENSE';
  semanticType: SemanticType;
  isSelf: boolean;
  /** Number of underlying transactions */
  count: number;
  /**
   * Net amount in accounting convention:
   *   positive = income (money in)
   *   negative = expense (money out)
   * Derived by negating Plaid amounts (Plaid: positive = expense).
   */
  totalAmount: number;
  /** Earliest transaction date in group (YYYY-MM-DD) */
  firstDate: string;
  /** Latest transaction date in group (YYYY-MM-DD) */
  lastDate: string;
  /** ISO currency code of the first transaction (groups are single-currency) */
  currency: string;
  /** Which normalization strategy produced the merchant name */
  merchantSource: NormalizationSource;
  /** Original transactions — used for drill-down and bulk actions */
  sourceTransactions: PlaidTransaction[];
}

// ─── Hook ──────────────────────────────────────────────────────────────────────

/**
 * Groups a flat list of PlaidTransactions into one canonical row per
 * (month × normalizedMerchant × direction × semanticType × isSelf).
 *
 * Invariant: sum of groupedRows.totalAmount === sum of input negated amounts
 *
 * Sort order: month desc → INCOME before EXPENSE → |totalAmount| desc
 */
export function useMerchantGrouping(data: PlaidTransaction[]): MerchantGroupedRow[] {
  return useMemo(() => {
    const map = new Map<string, MerchantGroupedRow>();

    for (const tx of data) {
      const normalizedTxDate = tx.date.slice(0, 10); // YYYY-MM-DD (strip time if present)
      const month = normalizedTxDate.slice(0, 7); // YYYY-MM
      const status = (tx.normalizationStatus || '').toUpperCase();
      // Use backend result only when it came from the real 3-layer normalizer
      // (DICTIONARY, RULES, or AI). SYNC_LEGACY is the old mapper value — worse
      // than the client-side normalizer for Zelle, fees, etc., so skip it.
      const hasRealBackendResult =
        tx.normalizedMerchant &&
        tx.normalizationSource &&
        tx.normalizationSource !== 'SYNC_LEGACY';
      const merchant = status === 'NON_MERCHANT'
        ? { key: 'non-merchant', display: 'Non-Merchant', source: 'NAME' as NormalizationSource }
        : hasRealBackendResult
        ? { key: tx.normalizedMerchant!.toLowerCase().trim(), display: tx.normalizedMerchant!, source: 'MERCHANT_NAME' as NormalizationSource }
        : normalizeMerchant(tx);
      const direction = tx.transactionClass; // already 'INCOME' | 'EXPENSE'
      const semanticType = deriveSemanticType(tx);
      const isSelf = deriveIsSelf(tx);

      const groupKey = `${month}|${merchant.key}|${direction}|${semanticType}|${isSelf}`;

      // Accounting convention: negate Plaid amount (Plaid positive = expense)
      const accountingAmount = -tx.amount;

      const existing = map.get(groupKey);
      if (existing) {
        existing.count += 1;
        existing.totalAmount += accountingAmount;
        if (normalizedTxDate < existing.firstDate) existing.firstDate = normalizedTxDate;
        if (normalizedTxDate > existing.lastDate) existing.lastDate = normalizedTxDate;
        existing.sourceTransactions.push(tx);
      } else {
        map.set(groupKey, {
          groupKey,
          month,
          normalizedMerchant: merchant.display,
          direction,
          semanticType,
          isSelf,
          count: 1,
          totalAmount: accountingAmount,
          firstDate: normalizedTxDate,
          lastDate: normalizedTxDate,
          currency: tx.isoCurrencyCode ?? tx.unofficialCurrencyCode ?? 'USD',
          merchantSource: merchant.source,
          sourceTransactions: [tx],
        });
      }
    }

    // ── Proximity canonicalization ───────────────────────────────────────────
    // Merge groups whose keys represent the same merchant (e.g. "adobe" and
    // "adobe creative cloud" produced by different normalization paths).
    const allMerchantKeys = new Set(Array.from(map.keys()).map(k => k.split('|')[1]));
    const canonical = canonicalizeKeys(allMerchantKeys);

    // Build a lookup: merchantKey → display name (for use when the canonical key
    // comes from a different group and we need its human-readable name)
    const keyToDisplay = new Map<string, string>();
    for (const row of map.values()) {
      const mKey = row.groupKey.split('|')[1];
      if (!keyToDisplay.has(mKey)) keyToDisplay.set(mKey, row.normalizedMerchant);
    }

    // Re-key the map: if a key's merchant part changes, merge into the canonical group
    const merged = new Map<string, MerchantGroupedRow>();
    for (const [groupKey, row] of map) {
      const parts = groupKey.split('|');
      const merchantKey = parts[1];
      const canonKey = canonical.get(merchantKey) ?? merchantKey;
      const canonDisplay = keyToDisplay.get(canonKey) ?? row.normalizedMerchant;
      const newGroupKey = [parts[0], canonKey, ...parts.slice(2)].join('|');

      const existing = merged.get(newGroupKey);
      if (existing) {
        existing.count += row.count;
        existing.totalAmount += row.totalAmount;
        if (row.firstDate < existing.firstDate) existing.firstDate = row.firstDate;
        if (row.lastDate > existing.lastDate) existing.lastDate = row.lastDate;
        existing.sourceTransactions.push(...row.sourceTransactions);
      } else {
        merged.set(newGroupKey, {
          ...row,
          groupKey: newGroupKey,
          normalizedMerchant: canonDisplay,
        });
      }
    }

    return [...merged.values()].sort((a, b) => {
      // 1. INCOME before EXPENSE
      if (a.direction !== b.direction) return a.direction === 'INCOME' ? -1 : 1;
      // 2. Merchant alphabetically within same direction
      const merchantCmp = a.normalizedMerchant.localeCompare(b.normalizedMerchant);
      if (merchantCmp !== 0) return merchantCmp;
      // 3. Month descending within same merchant+direction
      return b.month.localeCompare(a.month);
    });
  }, [data]);
}
